import { Filter2 } from "@layer/icons";
import { cn } from "@layer/utils/cn";
import type { Table } from "@tanstack/react-table";
import { type InputHTMLAttributes, useEffect, useState } from "react";
import { Button, Input } from "../../../components";
import { ActiveFilter } from "../active-filter";
import { DataTableFilter } from "../data-table-filter";
import { DataTableSettings } from "../data-table-settings";

interface DataTableToolbarProps<TData> {
	table: Table<TData>;
	globalFilterValue: string | number;
	onChange: (value: string | number) => void;
	color: string;
	inputColor?: string;
	isRunbook?: boolean;
	debounce?: number;
	inputPlaceholder?: string;
	filterField?: string;
}

const Toolbar = <TData,>({
	table,
	globalFilterValue: initialValue,
	onChange,
	color,
	inputColor,
	isRunbook,
	debounce = 500,
	inputPlaceholder = "Search across table...",
	filterField,
}: DataTableToolbarProps<TData> &
	Omit<InputHTMLAttributes<HTMLInputElement>, "onChange">) => {
	const isFiltered = table.getState().columnFilters?.length > 0;
	const isFilteredOverMultipleColumns =
		table.getState().columnFilters?.length > 0;
	const [value, setValue] = useState(initialValue);
	useEffect(() => {
		setValue(initialValue);
	}, [initialValue]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			onChange(value);
		}, debounce);

		return () => clearTimeout(timeout);
	}, [value, debounce, onChange]);

	return (
		<div
			className={cn(
				isRunbook
					? "h-14 flex items-start justify-between pl-1 pr-1"
					: "h-12 flex items-start justify-between pl-1 pr-1",
				color,
			)}
		>
			<section className="flex w-full items-center gap-x-2 overflow-clip">
				<Input
					placeholder={inputPlaceholder || "Search across table..."}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					className={cn(
						inputColor ?? "bg-input-bg/30",
						isRunbook && "border-app-border",
						"focus-visible:shadow-none focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-[var(--input-border-focus)]",
					)}
				/>
				<section className="flex items-center justify-start">
					{isFilteredOverMultipleColumns && (
						<span className="min-w-max px-2">
							<div className="bg-app-brand h-1 w-1 rounded-full" />
						</span>
					)}
					{table
						.getAllColumns()
						.filter((column) => column.columnDef.meta)
						.map(({ columnDef: { meta } }) => (
							<ActiveFilter
								key={meta?.id}
								column={table.getColumn(meta?.id)}
								title={meta?.label}
							/>
						))}
				</section>
				{isFiltered && (
					<Button
						variant="ghost"
						onClick={() => table.resetColumnFilters()}
					>
						<span className="text-accent-text! hover:text-accent-text-contrast border-none bg-transparent shadow-none! hover:bg-transparent">
							<Filter2 className="text-danger-text h-4 w-4" />
						</span>
					</Button>
				)}
			</section>
			{filterField && (
				<DataTableFilter table={table} filterField={filterField} />
			)}
			<DataTableSettings table={table} />
		</div>
	);
};

export { Toolbar };
